const PDF_HEADER = ascii("%PDF-");
const PDF_EOF = ascii("%%EOF");
const JPEG_HEADER = new Uint8Array([0xff, 0xd8, 0xff]);
const JPEG_EOF = new Uint8Array([0xff, 0xd9]);
const PNG_HEADER = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
const WEBP_RIFF = ascii("RIFF");
const WEBP_MARKER = ascii("WEBP");
const WEBP_CHUNK_MARKERS = [ascii("VP8 "), ascii("VP8L"), ascii("VP8X")];
const ZIP_LOCAL_HEADER = new Uint8Array([0x50, 0x4b, 0x03, 0x04]);
const ZIP_END_OF_CENTRAL_DIRECTORY = new Uint8Array([0x50, 0x4b, 0x05, 0x06]);
const DOCX_CONTENT_TYPES = ascii("[Content_Types].xml");
const DOCX_PACKAGE_RELATIONSHIPS = ascii("_rels/.rels");
const DOCX_DOCUMENT = ascii("word/document.xml");

const XML_MIME_TYPES = new Set(["application/xml", "text/xml"]);
const MAX_TEXT_SIGNATURE_BYTES = 64 * 1024;

function ascii(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function startsWith(bytes: Uint8Array, signature: Uint8Array, offset = 0): boolean {
  if (offset < 0 || bytes.byteLength < offset + signature.byteLength) return false;
  for (let index = 0; index < signature.byteLength; index += 1) {
    if (bytes[offset + index] !== signature[index]) return false;
  }
  return true;
}

function endsWith(bytes: Uint8Array, signature: Uint8Array): boolean {
  return startsWith(bytes, signature, bytes.byteLength - signature.byteLength);
}

function includes(bytes: Uint8Array, signature: Uint8Array, start = 0): boolean {
  const lastStart = bytes.byteLength - signature.byteLength;
  for (let offset = Math.max(0, start); offset <= lastStart; offset += 1) {
    if (startsWith(bytes, signature, offset)) return true;
  }
  return false;
}

function matchesPdf(bytes: Uint8Array): boolean {
  if (!startsWith(bytes, PDF_HEADER)) return false;
  return includes(bytes, PDF_EOF, Math.max(0, bytes.byteLength - 1_024));
}

function matchesJpeg(bytes: Uint8Array): boolean {
  return startsWith(bytes, JPEG_HEADER) && endsWith(bytes, JPEG_EOF);
}

function matchesWebp(bytes: Uint8Array): boolean {
  if (!startsWith(bytes, WEBP_RIFF) || !startsWith(bytes, WEBP_MARKER, 8)) return false;
  if (bytes.byteLength < 12) return false;
  const declaredPayloadLength = bytes[4]
    + (bytes[5] << 8)
    + (bytes[6] << 16)
    + (bytes[7] * 2 ** 24);
  return declaredPayloadLength + 8 === bytes.byteLength
    && WEBP_CHUNK_MARKERS.some((marker) => startsWith(bytes, marker, 12));
}

function decodeTextPrefix(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(
      bytes.subarray(0, MAX_TEXT_SIGNATURE_BYTES),
    );
  } catch {
    return null;
  }
}

function decodeText(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

function matchesXml(bytes: Uint8Array): boolean {
  // XML uploads are already bounded at every ingress. Inspect the complete
  // bounded payload so declarations cannot be hidden after a safe-looking prefix.
  const decoded = decodeText(bytes);
  if (decoded === null || decoded.includes("\u0000")) return false;
  const prefix = decoded.replace(/^\uFEFF/, "").trimStart();
  if (/<!DOCTYPE\b/i.test(prefix) || /<!ENTITY\b/i.test(prefix)) return false;
  return /^<\?xml(?:\s|\?>)/i.test(prefix)
    || /^<[A-Za-z_][A-Za-z0-9_.:-]*(?:\s|\/?>)/.test(prefix);
}

function matchesCsv(bytes: Uint8Array): boolean {
  const decoded = decodeTextPrefix(bytes);
  if (decoded === null || decoded.includes("\u0000")) return false;
  const prefix = decoded.replace(/^\uFEFF/, "").trimStart();
  return !/^<(?:!doctype|html|script|svg)\b/i.test(prefix);
}

function matchesDocx(bytes: Uint8Array): boolean {
  return startsWith(bytes, ZIP_LOCAL_HEADER)
    && includes(bytes, ZIP_END_OF_CENTRAL_DIRECTORY)
    && includes(bytes, DOCX_CONTENT_TYPES)
    && includes(bytes, DOCX_PACKAGE_RELATIONSHIPS)
    && includes(bytes, DOCX_DOCUMENT);
}

export function matchesDeclaredUploadType(body: ArrayBuffer, contentType: string): boolean {
  const bytes = new Uint8Array(body);
  if (bytes.byteLength === 0) return false;

  switch (contentType) {
    case "application/pdf":
      return matchesPdf(bytes);
    case "image/jpeg":
      return matchesJpeg(bytes);
    case "image/png":
      return startsWith(bytes, PNG_HEADER);
    case "image/webp":
      return matchesWebp(bytes);
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return matchesDocx(bytes);
    case "text/csv":
      return matchesCsv(bytes);
    default:
      return XML_MIME_TYPES.has(contentType) && matchesXml(bytes);
  }
}
